import '../styles/globals.css';
import CookieConsentBanner from '../components/CookieConsentBanner';
import { CustomerAuthProvider } from '../context/CustomerAuthContext';
import { CartProvider } from '../context/CartContext';
import { CookieConsentProvider } from '../context/CookieConsentContext';
import { WishlistProvider } from '../context/WishlistContext';
import { SmoothScrollProvider } from '../context/SmoothScrollContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PixelTracker from '../components/PixelTracker';
import SiteBackground from '../components/SiteBackground';
import FloatingWhatsAppButton from '../components/FloatingWhatsAppButton';
import LocalBusinessSchema from '../components/LocalBusinessSchema';
import PwaRegistration from '../components/PwaRegistration';
import TrackingScripts from '../components/TrackingScripts';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import Head from 'next/head';
import Script from 'next/script';
import { Poppins, Playfair_Display } from 'next/font/google';
import { AnimatePresence } from 'framer-motion';
import { SEO_DEFAULTS, getFacebookAppId } from '@/lib/seo';

const poppins = Poppins({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700', '800'],
  variable: '--font-poppins',
});

const playfair = Playfair_Display({
  subsets: ['latin'],
  weight: ['400', '500', '700', '800'],
  variable: '--font-playfair',
});

export default function App({ Component, pageProps, router }) {
  const isProductLandingPage =
    router.route === '/landing/[...productPath]' ||
    String(router.asPath || '').startsWith('/landing/');
  const isThankYouPage = router.route === '/thank-you';
  const showFloatingWhatsApp = router.route !== '/admin' && !isProductLandingPage;
  const showConsentPrompts = !isProductLandingPage && !isThankYouPage;
  const noIndexRoutes = ['/admin', '/cart', '/checkout', '/wishlist', '/account', '/order-success', '/thank-you'];
  const shouldNoIndex = noIndexRoutes.includes(router.route);
  const facebookAppId = getFacebookAppId();

  return (
    <HelmetProvider>
      <div className={`${poppins.variable} ${playfair.variable} relative min-h-screen overflow-x-hidden bg-transparent`}>
        <Head>
          <title>{SEO_DEFAULTS.defaultTitle}</title>
          <meta key="description" name="description" content={SEO_DEFAULTS.defaultDescription} />
          <meta key="robots" name="robots" content={shouldNoIndex ? 'noindex, nofollow' : 'index, follow'} />
          <meta key="application-name" name="application-name" content={SEO_DEFAULTS.siteName} />
          <meta key="theme-color" name="theme-color" content={SEO_DEFAULTS.themeColor} />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-status-bar-style" content="default" />
          <meta name="apple-mobile-web-app-title" content="Trendy Quest" />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta key="og:site_name" property="og:site_name" content={SEO_DEFAULTS.siteName} />
          <meta key="og:locale" property="og:locale" content={SEO_DEFAULTS.locale} />
          {facebookAppId ? <meta key="fb:app_id" property="fb:app_id" content={facebookAppId} /> : null}
          <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
          <meta name="p:domain_verify" content="cbd42e52e29c0763a2231bd2a737711c" />
          <link rel="manifest" href="/manifest.webmanifest" />
          <link rel="icon" href="/logo-yatiky.png" />
          <link rel="shortcut icon" href="/logo-yatiky.png" />
          <link rel="apple-touch-icon" href="/icons/apple-touch-icon.png" />
        </Head>
        <Script
          id="lottie-web-cdn"
          src="https://cdn.jsdelivr.net/npm/lottie-web@5.13.0/build/player/lottie.min.js"
          strategy="lazyOnload"
        />

        <PwaRegistration />
        <CookieConsentProvider>
          <TrackingScripts />
          <LocalBusinessSchema />
          {showConsentPrompts && <CookieConsentBanner />}
          {!isProductLandingPage && <SiteBackground />}
          <SmoothScrollProvider>
            <CustomerAuthProvider>
              <PixelTracker />
              <CartProvider>
                <WishlistProvider>
                  <div className="relative z-10">
                    {!isProductLandingPage && <Navbar />}
                    <AnimatePresence mode="wait">
                      <Component key={router.route} {...pageProps} />
                    </AnimatePresence>
                    {!isProductLandingPage && <Footer />}
                  </div>
                  {showFloatingWhatsApp && <FloatingWhatsAppButton />}
                  <Toaster position="bottom-right" />
                </WishlistProvider>
              </CartProvider>
            </CustomerAuthProvider>
          </SmoothScrollProvider>
        </CookieConsentProvider>
      </div>
    </HelmetProvider>
  );
}
